import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useImperativeHandle,
  useCallback,
} from "react";
import { useDebouncedState } from "@mantine/hooks";

import _ from "lodash";
import { Button, Select, MultiSelect, Box, Group, Paper } from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useForceUpdate } from "@mantine/hooks";
import ReactJson from "react-json-view";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import MultiRemoteSelect from "./multiRemoteSelect";
import FormReadOnlyField from "./formReadOnlyField";
import { formatFormFieldDisplay } from "../helpers/formatFormFieldDisplay";
import { DATA_AGE_TYPES } from "../data/options";
import { useTranslation } from "react-i18next";
// window.addEventListener("error", function (e) {
//   console.error(e.message);
//   // prevent React's listener from firing
//   e.stopImmediatePropagation("ResizeObserver loop limit exceeded");
//   // prevent the browser's console error message
//   e.preventDefault();
// });

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

const resizeObserver = new ResizeObserver(
  debounce((entries) => {
    for (let entry of entries) {
      // Handle resize
    }
  }, 100) // Adjust the debounce time as needed
);

const RemoteSelect2 = (
  {
    form,
    name,
    label,
    value,
    sort,
    apiEntity,
    valueField = "_id",
    labelField,
    labelRender,
    placeholder = "Pick One",
    clearable = true,
    searchFields = ["code"],
    preQuery,
    // required,
    multiple = false,
    onDataChange,
    onKeydown,
    clearAfterChange = false,
    disabled = false,
    readOnly = false,
    variant = "default",
    pageSize = 20,
    zIndex = 1000,
    width = "100%",
    size = "sm",
    select,
    onChange: onChangeProp,
    ...props
  },
  ref
) => {
  useImperativeHandle(
    ref,
    () => {
      return {
        clear: clearSelect,
      };
    },
    []
  );
  const [fieldValue, setFieldValue] = useState(); //selected value
  const [options, setOptions] = useState([]); //Converted to Options
  const [data, setData] = useState([]); //Orginal data sets
  const [searchText, setSearchText] = useDebouncedState("", 200);

  const [searchQuery, setSearchQuery] = useState({});
  const [api] = useServerApi();

  const currentPage = 1; //Always display first page
  const { t: translate } = useTranslation();
  const locked = disabled || readOnly || Boolean(form?.isReadOnly?.());
  const getDisplayValue = () => {
    let v = value || _.get(form?.values, name);
    if (!v) return null;

    if (!multiple) {
      // console.log("getDisplayValue1", name, v);
      v = typeof v === "object" ? v?._id : v;
    }
    if (multiple && Array.isArray(v)) {
      // console.log("getDisplayValue2", name, v);

      v = v.map((value) => value?._id || value);
    }
    return v;
  };

  const displayValue = getDisplayValue();

  useDeepCompareEffect(() => {
    setFieldValue(displayValue);
  }, [displayValue]);

  const handleChange = useCallback((v) => {
    // console.log("handleChange", v);
    // Use requestAnimationFrame to defer state updates
    // This prevents ResizeObserver loop errors when clearing/selecting
    requestAnimationFrame(() => {
      setFieldValue(v);
      setFormValue(v);
      notifyDataChange(v);
      onChangeProp?.(v);
    });
  });

  const clearSelect = () => {
    setFieldValue(null);
  };

  const setFormValue = (v) => {
    if (!form || !name) return;
    // if (!value || _.isEmpty(value)) return;
    form.markUserEdited?.();
    form.setFieldValue(name, v);
  };

  const notifyDataChange = (v) => {
    if (!_.isFunction(onDataChange)) return;
    
    // Find the data for the selected value
    const selectedData = data.find((d) => _.get(d, valueField) === v);
    
    // Call onDataChange with the found data (or null if not found/cleared)
    // Use requestAnimationFrame to avoid ResizeObserver loop during layout changes
    requestAnimationFrame(() => {
      onDataChange(selectedData || null, form);
    });
    
    if (clearAfterChange) {
      setFieldValue(null);
    }
  };

  const getLabel = (data) => {
    // console.log("getLabel", name, data);
    if (labelRender) return labelRender(data);
    if (labelField) return _.get(data, labelField);
    return "";
  };

  const fetchFieldValueData = async () => {
    if (!fieldValue) return;
    // console.log("fetchFieldValueData before", name, options, fieldValue);
    if (options.findIndex((o) => o.value === fieldValue) !== -1) return;

    let result = await api.search({
      apiEntity,
      pageSize,
      currentPage: 1,
      sort,
      searchQuery: { [valueField]: fieldValue },
      select,
      byAggregation: false,
    });
    // console.log("fetchFieldValueData", name, result);
    const rows = result?.docs;
    if (!rows || _.isEmpty(rows)) return;

    let o = rows.map((r) => ({
      value: _.get(r, valueField),
      label: getLabel(r),
    }));
    setOptions([...o, ...options]);
  };

  const fetchData = async () => {
    try {
      if (_.isEmpty(searchQuery) && !_.isEmpty(preQuery)) return;

      // console.log("fetchData", searchQuery);
      let result = await api.search({
        apiEntity,
        pageSize,
        currentPage,
        sort,
        searchQuery,
        byAggregation: false,
        select,
      });

      handleFetchResult(result?.docs ?? []);
      fetchFieldValueData();
    } catch (e) {
      // Preserve the currently selected option even on error
      setOptions((prevOptions) => {
        if (!fieldValue) return [];
        const currentOption = prevOptions.find((o) => o.value === fieldValue);
        return currentOption ? [currentOption] : [];
      });
      setData([]);
    }
  };

  const handleFetchResult = (rows) => {
    const safe = Array.isArray(rows) ? rows : [];
    setData(safe);
    const newOptions = safe.map((r) => ({
      value: _.get(r, valueField),
      label: getLabel(r),
    }));
    
    // Preserve the currently selected option if it exists
    // This prevents the clear button from flickering during search
    setOptions((prevOptions) => {
      if (!fieldValue) return newOptions;
      
      // Check if current selection is in new options
      const currentInNew = newOptions.find((o) => o.value === fieldValue);
      if (currentInNew) return newOptions;
      
      // Keep the current selection from previous options
      const currentOption = prevOptions.find((o) => o.value === fieldValue);
      if (currentOption) {
        return [currentOption, ...newOptions];
      }
      
      return newOptions;
    });
  };

  const handleRegexSpecialChar = (text) => {
    // console.log("handleRegexSpecialChar", text);
    const specialTxt = [
      "\\",
      "^",
      "$",
      ".",
      "|",
      "?",
      "*",
      "+",
      "(",
      ")",
      "[",
      "]",
      "{",
      "}",
    ];

    let result = text;
    specialTxt.forEach((special) => {
      result = result.replace(special, `\\${special}`);
    });
    return result;
  };

  const buildSearchQuery = (searchText, searchFields) => {
    //Remove sepecical character
    // const txt = const searchText.replace(")", "\\)").replace("(", "\\(");

    if (!searchText) {
      return preQuery ? preQuery : {};
    }

    let txt = handleRegexSpecialChar(searchText)?.trim();
    let searchArr = searchFields?.map((field) => ({
      [field]: { $regex: txt, $options: "i" },
    }));
    if (!preQuery) return { $or: searchArr };
    return { $and: [preQuery, { $or: searchArr }] };

    //split the search text by space
    // let txtArr = searchText.split(" ");
    // txtArr = txtArr.map((txt) => handleRegexSpecialChar(txt)?.trim());

    // let searchArr = [];
    // txtArr.forEach((txt) => {
    //   searchArr = [
    //     ...searchArr,
    //     ...searchFields?.map((field) => ({
    //       [field]: { $regex: txt, $options: "i" },
    //     })),
    //   ];
    // });

    // if (!preQuery) return { $or: searchArr };
    // return { $and: [preQuery, { $or: searchArr }] };
  };

  const handleSearchTextChange = (text) => {
    setSearchText(text);
  };

  useEffect(() => {
    let query = buildSearchQuery(searchText, searchFields);
    setSearchQuery(query);
  }, [searchText]);

  // const handleSearchChange = useCallback(
  //   (searchText) => {
  //     let query = buildSearchQuery(searchText, searchFields);
  //     setSearchQuery(query);
  //   },
  //   [searchFields, searchText]
  // );

  useEffect(() => {
    if (locked) return;
    fetchData();
  }, [searchQuery]);

  useEffect(() => {
    fetchFieldValueData();
  }, [fieldValue]);

  if (locked) {
    const raw = value ?? _.get(form?.values, name);
    const fromOptions = multiple
      ? (Array.isArray(fieldValue) ? fieldValue : [])
          .map(
            (item) =>
              options.find((option) => option.value === item)?.label
          )
          .filter(Boolean)
          .join(", ")
      : options.find((option) => option.value === fieldValue)?.label;
    const formatted = formatFormFieldDisplay(raw, {
      kind: "remoteSelect",
      props: { labelField, labelRender },
    });
    return (
      <FormReadOnlyField
        label={label}
        value={fromOptions || formatted}
      />
    );
  }

  return (
    <>
      {/* {searchText} */}
      {/* fieldValue:{JSON.stringify(fieldValue)}
      displayValue: {JSON.stringify(displayValue)} <br></br> */}
      {/* multiple: {multiple ? "true" : "false"} */}
      {/* sort: {JSON.stringify(sort)} <br></br>  */}
      {/* {pageSize} */}
      {multiple ? (
        <MultiRemoteSelect
          form={form}
          name={name}
          label={label}
          value={fieldValue ?? value}
          onChange={onChangeProp}
          sort={sort}
          pageSize={pageSize}
          apiEntity={apiEntity}
          valueField={valueField}
          labelField={labelField}
          labelRender={labelRender}
          searchFields={searchFields}
          required={props.required}
          preQuery={preQuery}
          size={size}
          placeholder={placeholder}
          disabled={disabled}
          {...props}
        />
      ) : (
        /* <MultiSelect
						// {...props}
						value={fieldValue}
						label={label}
						placeholder={placeholder}
						clearable
						searchable
						onChange={handleChange}
						onSearchChange={handleSearchChange}
						onKeyDown={onKeydown}
						data={options}
						disabled={disabled}
						variant={variant}
					/> */

        <Select
          {...props}
          value={fieldValue}
          label={label}
          placeholder={translate(placeholder)}
          clearable={true}
          required={props.required}
          searchable
          onChange={handleChange}
          onSearchChange={handleSearchTextChange}
          onKeyDown={onKeydown}
          data={options}
          disabled={disabled}
          variant={variant}
          searchFields={searchFields}
          zIndex={zIndex}
          width={width}
          size={size}
        />
      )}
    </>
  );
};

export default React.forwardRef(RemoteSelect2);
